import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { PaymentJob } from '@/types/reports';
import { formatInTz, DEFAULT_TIMEZONE } from '@/utils/timezone';
import { useNavigate } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface PaymentJobsSheetProps {
    title: string;
    jobs: PaymentJob[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    timezone?: string;
}

export const PaymentJobsSheet = ({ title, jobs, open, onOpenChange, timezone = DEFAULT_TIMEZONE }: PaymentJobsSheetProps) => {
    const navigate = useNavigate();
    const { formatMoney: formatCurrency } = useCompanyLocale();

    const handleJobClick = (jobId: string) => {
        onOpenChange(false);
        navigate(`/jobs/${jobId}`);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="w-[400px] sm:w-[540px] flex flex-col p-0">
                <div className="p-6 border-b">
                    <SheetHeader>
                        <SheetTitle>{title}</SheetTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            {jobs.length} jobs
                        </p>
                    </SheetHeader>
                </div>

                <ScrollArea className="flex-1">
                    <div className="p-6 space-y-4">
                        {jobs.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-8">
                                No jobs found in this period.
                            </p>
                        ) : (
                            jobs.map(job => (
                                <div
                                    key={job.jobId}
                                    className="p-4 border rounded-lg hover:border-primary/50 cursor-pointer transition-colors bg-card relative group"
                                    onClick={() => handleJobClick(job.jobId)}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-semibold text-primary">{job.jobNumber}</span>
                                            </div>
                                            <p className="text-sm font-medium">{job.customerName}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-lg text-foreground">
                                                {formatCurrency(job.amount)}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {job.closedDate ? formatInTz(new Date(job.closedDate), timezone, 'MMM d, h:mm a') : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </ScrollArea>
            </SheetContent>
        </Sheet>
    );
};
